import { useEffect, useState } from "react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, X, Plus } from "lucide-react";

export const ABSENCE_KINDS = [
  { value: "vacation", label: "Dovolená", color: "#376A4D" },
  { value: "doctor", label: "Lékař", color: "#3D6FB8" },
  { value: "sick", label: "Nemoc", color: "#9E4747" },
  { value: "other", label: "Jiné", color: "#57534E" },
];

export function absenceKindMeta(kind) {
  return ABSENCE_KINDS.find((k) => k.value === kind) || ABSENCE_KINDS[3];
}

function todayISO() {
  return new Date().toISOString().slice(0, 10);
}

/**
 * Dialog pro vložení nepřítomnosti.
 * Props:
 *  - open: bool
 *  - onClose: ()=>void
 *  - onSaved: ()=>void  (load list after save)
 *  - users?: array  – pokud zadáno (admin/manager), zobrazí výběr zaměstnance
 *  - defaultDate?: string YYYY-MM-DD
 */
export default function AbsenceDialog({ open, onClose, onSaved, users, defaultDate }) {
  const [form, setForm] = useState({
    user_id: "",
    kind: "vacation",
    date: defaultDate || todayISO(),
    full_day: true,
    from_time: "08:00",
    to_time: "16:00",
    note: "",
  });
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, date: defaultDate || todayISO() }));
    }
  }, [open, defaultDate]);

  if (!open) return null;

  const save = async () => {
    if (!form.date) {
      toast.error("Vyberte datum");
      return;
    }
    if (!form.full_day && (!form.from_time || !form.to_time)) {
      toast.error("Zadejte čas od / do");
      return;
    }
    if (users && !form.user_id) {
      toast.error("Vyberte zaměstnance");
      return;
    }
    setBusy(true);
    try {
      const payload = {
        kind: form.kind,
        date: form.date,
        full_day: form.full_day,
        from_time: form.full_day ? null : form.from_time,
        to_time: form.full_day ? null : form.to_time,
        note: form.note || "",
      };
      if (users && form.user_id) payload.user_id = form.user_id;
      await api.post("/attendance/absences", payload);
      toast.success("Nepřítomnost zaznamenána");
      onClose();
      if (onSaved) onSaved();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Chyba");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div
      data-testid="absence-dialog"
      className="fixed inset-0 z-50 grid place-items-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-lg shadow-2xl max-w-md w-full p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div>
            <div className="label-eyebrow mb-1">Nová nepřítomnost</div>
            <h3 className="text-xl font-semibold flex items-center gap-2" style={{ fontFamily: "Outfit" }}>
              <CalendarDays className="w-5 h-5 text-[#3A2D27]" /> Záznam nepřítomnosti
            </h3>
          </div>
          <button onClick={onClose} className="p-2 -mr-2 -mt-2 rounded hover:bg-[#F9F6F0]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="space-y-3">
          {users && (
            <label className="block">
              <div className="text-sm font-medium mb-1.5">Zaměstnanec</div>
              <select
                data-testid="absence-user"
                className="input-cafe w-full"
                value={form.user_id}
                onChange={(e) => setForm({ ...form, user_id: e.target.value })}
              >
                <option value="">— vyberte —</option>
                {users.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name || u.username}
                  </option>
                ))}
              </select>
            </label>
          )}

          <div>
            <div className="text-sm font-medium mb-1.5">Typ</div>
            <div className="flex flex-wrap gap-1.5">
              {ABSENCE_KINDS.map((k) => (
                <button
                  key={k.value}
                  data-testid={`absence-kind-${k.value}`}
                  type="button"
                  onClick={() => setForm({ ...form, kind: k.value })}
                  className={`px-3 py-1.5 text-sm rounded-md border transition-colors ${
                    form.kind === k.value
                      ? "bg-[#3A2D27] text-white border-[#3A2D27]"
                      : "border-[#E5E1D8] hover:bg-[#F9F6F0]"
                  }`}
                >
                  {k.label}
                </button>
              ))}
            </div>
          </div>

          <label className="block">
            <div className="text-sm font-medium mb-1.5">Datum</div>
            <input
              data-testid="absence-date"
              type="date"
              className="input-cafe w-full"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
            />
          </label>

          <label className="flex items-center gap-2 select-none cursor-pointer">
            <input
              data-testid="absence-fullday"
              type="checkbox"
              checked={form.full_day}
              onChange={(e) => setForm({ ...form, full_day: e.target.checked })}
              className="w-4 h-4"
            />
            <span className="text-sm">Celý den</span>
          </label>

          {!form.full_day && (
            <div className="grid grid-cols-2 gap-3">
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Od</div>
                <input
                  data-testid="absence-from"
                  type="time"
                  className="input-cafe w-full"
                  value={form.from_time}
                  onChange={(e) => setForm({ ...form, from_time: e.target.value })}
                />
              </label>
              <label className="block">
                <div className="text-sm font-medium mb-1.5">Do</div>
                <input
                  data-testid="absence-to"
                  type="time"
                  className="input-cafe w-full"
                  value={form.to_time}
                  onChange={(e) => setForm({ ...form, to_time: e.target.value })}
                />
              </label>
            </div>
          )}

          <label className="block">
            <div className="text-sm font-medium mb-1.5">Poznámka</div>
            <input
              data-testid="absence-note"
              type="text"
              placeholder="(volitelné)"
              className="input-cafe w-full"
              value={form.note}
              onChange={(e) => setForm({ ...form, note: e.target.value })}
            />
          </label>
        </div>

        <div className="mt-5 flex justify-end gap-2">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded-md border border-[#E5E1D8] hover:bg-[#F9F6F0]"
          >
            Zrušit
          </button>
          <button
            data-testid="absence-save"
            disabled={busy}
            onClick={save}
            className="btn-primary inline-flex items-center gap-1.5"
          >
            <Plus className="w-4 h-4" /> {busy ? "Ukládám…" : "Uložit"}
          </button>
        </div>
      </div>
    </div>
  );
}

/**
 * List jednotlivých nepřítomností. Zobrazuje typ, datum, čas, jméno (pokud showUser).
 */
export function AbsenceList({ items, onDelete, showUser = false, emptyText = "Žádné záznamy." }) {
  if (!items || items.length === 0) {
    return <div className="text-sm text-[#57534E]">{emptyText}</div>;
  }
  return (
    <ul className="space-y-2">
      {items.map((a) => {
        const k = absenceKindMeta(a.kind);
        return (
          <li
            key={a.id}
            data-testid={`absence-item-${a.id}`}
            className="bg-white border border-[#E5E1D8] rounded-md p-3 flex items-start gap-3 flex-wrap"
          >
            <span
              className="text-xs font-medium px-2 py-0.5 rounded-full border"
              style={{
                color: k.color,
                borderColor: k.color + "66",
                background: k.color + "1A",
              }}
            >
              {k.label}
            </span>
            <div className="flex-1 min-w-0">
              {showUser && a.user_name && (
                <div className="text-sm font-semibold text-[#3A2D27] truncate mb-0.5">
                  {a.user_name}
                </div>
              )}
              <div className="text-sm">
                <span className={showUser ? "text-[#57534E]" : "font-medium"}>{a.date}</span>
                {!a.full_day && a.from_time && (
                  <>
                    {" "}
                    <span className="text-[#57534E]">{a.from_time}–{a.to_time}</span>
                  </>
                )}
                {a.full_day && <span className="text-[#57534E]"> · celý den</span>}
              </div>
              {a.note && <div className="text-xs text-[#57534E] mt-0.5"><em>{a.note}</em></div>}
            </div>
            {onDelete && (
              <button
                data-testid={`absence-delete-${a.id}`}
                onClick={() => {
                  if (!window.confirm("Smazat záznam nepřítomnosti?")) return;
                  onDelete(a);
                }}
                className="p-2 rounded-md border border-[#E5E1D8] text-[#9E4747] hover:bg-[#9E47471A]"
                title="Smazat"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </li>
        );
      })}
    </ul>
  );
}
